import React, { ReactNode, useContext, useState } from 'react';
import styled from 'styled-components/macro';
import { useApiRequest } from '../../api/useApiRequest';
import { PrimaryButton, SecondaryButton } from '../../shared/Button';
import { ErrorBox } from '../../shared/ErrorBox';
import { MultiSearchSelect, MultiSelectOptionType } from '../../shared/form/inputs/MultiSearchSelect';
import { PageLoadingIndicator } from '../../shared/LoadingIndicator';
import { TextInput } from '../../shared/TextInput';
import { spacing02, spacing05 } from '../../styling/spacing';
import { getPartIdsAndDockingTypeCodesFromSelectedParts } from './documentFormModel';
import {
  GetHandoverPackSectionsByFilterResponse,
  getHandoverPackSectionMultiSelectOptions,
  getPartMultiSelectOptions,
  HandoverPackSectionResponse,
} from './documentResponse';
import { HandoverPackSectionContext } from './documents-section/HandoverPackSectionContext';

type Props = {
  children: (props: DocumentFiltersResponse) => ReactNode;
};

type DocumentFiltersResponse = {
  filteredDocumentSections: Array<HandoverPackSectionResponse>;
};

type FilterQueryDto = {
  searchTerm: string;
  handoverPackSectionIds: Array<number>;
  partIds: Array<number>;
  dockingTypeCodes: Array<string>;
  technologyTypes: Array<HandoverPackTechnologyTypeCode>;
};

type HandoverPackTechnologyTypeCode =
  | 'UserSuppliedHeatPump'
  | 'NuHeatSuppliedHeatPump'
  | 'Solar'
  | 'UnderfloorHeating'
  | 'Boiler'
  | 'Any';

export const DocumentListFilters = ({ children }: Props) => {
  const [selectedParts, setSelectedParts] = useState<Array<MultiSelectOptionType<string>>>([]);
  const [selectedSections, setSelectedSections] = useState<Array<MultiSelectOptionType<number>>>([]);
  const [selectedTechnologyTypes, setSelectedTechnologyTypes] = useState<
    Array<MultiSelectOptionType<HandoverPackTechnologyTypeCode>>
  >([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [useFilteredList, setUseFilteredList] = useState<boolean>(false);
  const [filteredSections, setFilteredSections] = useState<Array<HandoverPackSectionResponse>>([]);

  const { handoverPackSections, parts } = useContext(HandoverPackSectionContext);
  const sectionOptions = getHandoverPackSectionMultiSelectOptions(handoverPackSections);
  const partOptions = getPartMultiSelectOptions(parts);

  const { error, inProgress, sendRequest } = useApiRequest<GetHandoverPackSectionsByFilterResponse, FilterQueryDto>({
    httpMethod: 'post',
    endpointUrl: 'handover-pack/sections/filter',
  });

  const technologyTypeSelectOptions: Array<MultiSelectOptionType<HandoverPackTechnologyTypeCode>> = [
    { label: 'User Supplied Heat Pump', value: 'UserSuppliedHeatPump' },
    { label: 'Nu-Heat Supplied Heat Pump', value: 'NuHeatSuppliedHeatPump' },
    { label: 'Solar', value: 'Solar' },
    { label: 'Underfloor Heating', value: 'UnderfloorHeating' },
    { label: 'Boiler', value: 'Boiler' },
    { label: 'Any', value: 'Any' },
  ];

  const onSearchTermChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const onPartChange = (parts: Array<MultiSelectOptionType<string>>) => {
    setSelectedParts(parts);
  };

  const onSectionChange = (sections: Array<MultiSelectOptionType<number>>) => {
    setSelectedSections(sections);
  };

  const onTechnologyTypeChange = (technologyTypes: Array<MultiSelectOptionType<HandoverPackTechnologyTypeCode>>) => {
    setSelectedTechnologyTypes(technologyTypes);
  };

  const applyFilter = () => {
    setUseFilteredList(true);

    const { partIds, dockingTypeCodes } = getPartIdsAndDockingTypeCodesFromSelectedParts(selectedParts);

    void sendRequest({
      requestBody: {
        searchTerm,
        handoverPackSectionIds: selectedSections.map(section => section.value),
        partIds: partIds,
        dockingTypeCodes: dockingTypeCodes,
        technologyTypes: selectedTechnologyTypes.map(technologyType => technologyType.value),
      },
      onSuccess: response => {
        setFilteredSections(response.handoverPackSections);
      },
    });
  };

  const clearFilters = () => {
    setUseFilteredList(false);
    setSelectedParts([]);
    setSelectedSections([]);
    setSelectedTechnologyTypes([]);
    setSearchTerm('');
  };

  const RequestHandler = () => {
    if (inProgress) {
      return <PageLoadingIndicator />;
    }

    if (error) {
      return <ErrorBox error={error} />;
    }

    return <>{children({ filteredDocumentSections: useFilteredList ? filteredSections : handoverPackSections })}</>;
  };

  return (
    <>
      <QueryRow>
        <TextInput
          style={{ width: '20%' }}
          value={searchTerm}
          onChange={onSearchTermChange}
          placeholder="Search by name"
        />
        <MultiSearchSelect
          styles={{ width: '20%' }}
          onChange={onSectionChange}
          values={selectedSections}
          options={sectionOptions}
          placeholder="Search by handover pack section"
        />
        <MultiSearchSelect
          styles={{ width: '20%' }}
          onChange={onPartChange}
          values={selectedParts}
          options={partOptions}
          placeholder="Search by part numbers"
        />
        <MultiSearchSelect
          styles={{ width: '20%' }}
          onChange={onTechnologyTypeChange}
          values={selectedTechnologyTypes}
          options={technologyTypeSelectOptions}
          placeholder="Search by technology type"
        />
        <ButtonRow>
          <PrimaryButton onClick={applyFilter}>Apply Filters</PrimaryButton>
          <SecondaryButton onClick={clearFilters}>Clear All Filters</SecondaryButton>
        </ButtonRow>
      </QueryRow>
      <RequestHandler />
    </>
  );
};

const QueryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing05};
`;

const ButtonRow = styled.div`
  display: flex;
  width: fit-content;
  gap: ${spacing02};
`;
